import React, { useState } from "react";
import { toast } from "sonner";
import { apiError } from "@/lib/apiError";
import { ChevronDown, ChevronRight, Folder, FileText, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

/** Collapsible preview of the folder tree the Phase 1 script will build. */
export const SorterPreview = ({ projectId }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [tree, setTree] = useState(null);
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (tree) return;
    setBusy(true);
    try {
      const res = await api.get(`/projects/${projectId}/sorter/preview`);
      setTree(res.data);
    } catch (err) {
      toast.error(apiError(err, t("sorter_failed")));
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-[#1E1E1E] pt-5" data-testid="sorter-preview">
      <button data-testid="sorter-preview-toggle" onClick={toggle}
        className="flex items-center gap-2 min-h-[44px] font-mono-accent text-[10px] tracking-[0.18em] uppercase text-[#A8A8A8] hover:text-[#D4AF37] transition-colors">
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {t("sorter_preview_title")}
      </button>

      {open && (
        <div className="mt-5" data-testid="sorter-preview-tree">
          {busy && <Loader2 size={14} className="animate-spin text-[#707070]" />}
          {tree && (
            <div className="font-mono-accent text-[11px] leading-relaxed break-all">
              <p className="text-[#D4AF37]"><Folder size={12} className="inline mr-2 -mt-0.5" />{tree.root}/</p>

              <p className="mt-2 ml-4 text-[#F3F3F1]"><Folder size={12} className="inline mr-2 -mt-0.5" />EDIT/</p>
              {tree.edit.length === 0 && <p className="ml-10 text-[#5A5A5A]">{t("sorter_preview_empty")}</p>}
              {tree.edit.map((name) => (
                <p key={`e-${name}`} className="ml-10 text-[#A8A8A8]" data-testid={`sorter-preview-edit-${name}`}>
                  <FileText size={11} className="inline mr-2 -mt-0.5" />{name}.*
                </p>
              ))}

              <p className="mt-2 ml-4 text-[#F3F3F1]"><Folder size={12} className="inline mr-2 -mt-0.5" />CETAK/</p>
              {tree.print.length === 0 && <p className="ml-10 text-[#5A5A5A]">{t("sorter_preview_empty")}</p>}
              {tree.print.map((p) => (
                <p key={`p-${p.tagged}`} className="ml-10 text-[#A8A8A8]" data-testid={`sorter-preview-print-${p.source}`}>
                  <FileText size={11} className="inline mr-2 -mt-0.5" />{p.tagged}.*
                </p>
              ))}

              {tree.sizes.length > 0 && (
                <>
                  <p className="mt-5 text-[#D4AF37]">
                    <Folder size={12} className="inline mr-2 -mt-0.5" />{tree.master_dir}/
                    <span className="ml-2 text-[9px] tracking-[0.16em] text-[#5A5A5A]">{t("sorter_phase2")}</span>
                  </p>
                  {tree.sizes.map((size) => (
                    <p key={size} className="ml-10 text-[#A8A8A8]" data-testid={`sorter-preview-size-${size}`}>
                      <Folder size={11} className="inline mr-2 -mt-0.5" />{size}/
                    </p>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
